import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FloatingPortal } from "@floating-ui/react";
import { useCallback, useContext, useMemo, useRef } from "react";
import styled, { css } from "styled-components";
import { outlineBorderActiveBlueCSS, outlineBorderActiveRedCSS } from "../../common/outlineActive";
import { UISettingsProvider } from "../../core/context/UISettingsProvider";
import { useKeys } from "../../core/hooks/useKeys";
import { controlFontThemes, defaultControlSize, newControlWrapperCSS, } from "../newCommon/ControlsCommon";
import { FillByText } from "../newCommon/FillByText";
import { SelectDropdownOptions } from "./components/SelectDropdownOptions";
import { Arrow } from "./components/ToggleButton";
import { USelectDropdown } from "./components/UltraSelectDropdown";
import { USelectContext, USelectProviders } from "./context/context";
import { useDropdown } from "./hooks/useDropdown";
import { useUSelectOptions } from "./hooks/useUSelectOptions";
import { renderValueDefault } from "./renders/default";
import { optionsIsSame } from "./utils/utils";
const emptyFunction = () => undefined;
function isSimpleValue(v) {
    return typeof v === "string" || typeof v === "number";
}
export const USelectGeneralComponent = ({ renderOptions, renderOption, renderNoOptions, renderValue = renderValueDefault, renderHeader, options: _options, dropdownOffset, appendTo = "parent", dropdownPlacement, strategy, testId, ...props }) => {
    const { setShowDropdown, showDropdown } = useContext(USelectContext);
    const { size = defaultControlSize, className, error, disabled = false, readonly = false, edited = false, value: _value, onChange = emptyFunction, clearOption, selectAllOption, removeSelected = true, } = props;
    const value = useMemo(() => {
        if (_value) {
            const onlyObjectsValue = [];
            for (const value of _value) {
                const isSimple = isSimpleValue(value);
                const objectValue = isSimple ? _options?.find((option) => option.value === value) : value;
                if (objectValue) {
                    onlyObjectsValue.push(objectValue);
                }
            }
            return onlyObjectsValue;
        }
        else {
            return [];
        }
    }, [_options, _value]);
    const options = useUSelectOptions({
        value,
        options: _options,
        onChange,
        clearOption,
        selectAllOption,
        onCreate: props.onCreate,
    });
    const { getFloatingProps, getReferenceProps, refs, style } = useDropdown({
        dropdownOffset,
        dropdownPlacement,
        onOpenChange: setShowDropdown,
        size,
        strategy,
    });
    const getOptionIsActive = useCallback((option) => {
        return Boolean(value?.find((opt) => optionsIsSame(opt, option)));
    }, [value]);
    // NESTED OPTIONS MAP
    const optionsMap = useMemo(() => {
        const allOptionsMap = {};
        const getOptions = (options = []) => {
            options.forEach((option) => {
                allOptionsMap[option.value] = option;
            });
            for (const option of options) {
                if (Array.isArray(option.children)) {
                    getOptions(option.children);
                }
            }
        };
        getOptions([...(value || []), ...options]);
        return allOptionsMap;
    }, [value, options]);
    const handleChange = useCallback((event) => {
        if (disabled)
            return;
        if (event.type === "select") {
            return onChange?.(value ? [...value, event.newValue] : [event.newValue], event);
        }
        if (event.type === "remove") {
            return onChange?.((value || []).filter((v) => !optionsIsSame(v, event.oldValue)), event);
        }
    }, [disabled, onChange, value]);
    const handleControlClick = useCallback(() => {
        if (!disabled && !readonly && !props.hideDropdown) {
            setShowDropdown((prev) => !prev);
        }
    }, [disabled, readonly, props.hideDropdown, setShowDropdown]);
    const toggleOptionByValue = useCallback((value) => {
        const option = optionsMap[value];
        const isSelected = getOptionIsActive(option);
        if (!isSelected) {
            handleChange({
                type: "select",
                newValue: option,
            });
        }
        else if (removeSelected) {
            handleChange({
                type: "remove",
                oldValue: option,
            });
        }
    }, [getOptionIsActive, handleChange, optionsMap, removeSelected]);
    const rootRef = useRef(null);
    const dropdownRef = useRef(null);
    // ALL PROPERTIES FOR RENDERS
    const renderProps = {
        ...props,
        value,
        options,
        optionsMap,
        handleChange,
        getOptionIsActive,
        toggleOptionByValue,
        dropdownRef,
        controlRef: refs.reference,
    };
    const handleKeyDown = useKeys({
        Enter: () => setShowDropdown(true),
        Space: () => setShowDropdown(true),
        ArrowUp: () => setShowDropdown(true),
        ArrowDown: () => setShowDropdown(true),
    });
    const contextSettings = useContext(UISettingsProvider);
    return (_jsxs(Root, { className: className, ref: rootRef, "data-testid": testId, children: [_jsxs(Control, { ref: refs.setReference, ...getReferenceProps(), onKeyDown: handleKeyDown, "data-open": showDropdown, onClick: handleControlClick, "$disabled": disabled || readonly, "$invalid": error, "$edited": !error && edited, "$size": size, "$active": showDropdown, tabIndex: 0, children: [_jsx(ValueWrapper, { children: renderValue?.(renderProps) }), !props.hideArrow && _jsx(Arrow, { isOpen: showDropdown })] }), showDropdown && (_jsx(FloatingPortal, { root: appendTo === "parent" ? rootRef : contextSettings?.dropdowns?.appendTo, children: _jsx("div", { style: style, ref: refs.setFloating, ...getFloatingProps(), "data-testid": testId + "-dropdown", children: _jsx(USelectDropdown, { ...renderProps, renderHeader: renderHeader, renderNoOptions: renderNoOptions, renderOption: renderOption, renderOptions: renderOptions }) }) }))] }));
};
const Root = styled.div `
  display: flex;
  align-items: center;
  outline: none;

  &:focus,
  &:active {
    outline: none;
  }
`;
const ValueWrapper = styled.div `
  user-select: none;
  width: auto;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  flex: 1;
  outline: none;
  overflow: hidden;
  position: relative;

  &:focus {
    outline: none;
  }
`;
const InputWrapper = styled(FillByText) `
  margin-left: 12px;
  margin-right: 12px;
  max-width: 100%;
  overflow: hidden;
`;
const Tag = styled.span `
  display: flex;
  max-width: 100%;
`;
const themes = {
    "50": css `
    ${Tag} {
      padding: 2px;
    }

    ${InputWrapper} {
      min-height: 24px;
    }

    padding: 0 ${(p) => p.theme.spacing.sp8};

    ${Arrow} {
      padding-left: ${(p) => p.theme.spacing.sp8};

      svg {
        width: 12px;
        height: 12px;
      }
    }
  `,
    "100": css `
    ${Tag} {
      padding: 3px;
    }

    ${InputWrapper} {
      min-height: 30px;
    }

    padding: 0 ${(p) => p.theme.spacing.sp8};

    ${Arrow} {
      padding-left: ${(p) => p.theme.spacing.sp8};

      svg {
        width: 16px;
        height: 16px;
      }
    }
  `,
    "200": css `
    ${Tag} {
      padding: 4px;
    }

    ${InputWrapper} {
      min-height: 38px;
    }

    padding: 0 ${(p) => p.theme.spacing.sp8};

    ${Arrow} {
      padding-left: ${(p) => p.theme.spacing.sp8};

      svg {
        width: 16px;
        height: 16px;
      }
    }
  `,
    "400": css `
    ${Tag} {
      padding: 4px;
    }

    ${InputWrapper} {
      min-height: 38px;
    }

    padding: 0 ${(p) => p.theme.spacing.sp16};

    ${Arrow} {
      padding-left: ${(p) => p.theme.spacing.sp8};

      svg {
        width: 20px;
        height: 20px;
      }
    }
  `,
};
const Control = styled.div `
  ${newControlWrapperCSS};

  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  flex-wrap: nowrap;

  ${(p) => themes[p.$size || defaultControlSize]};

  ${ValueWrapper} {
    ${(p) => controlFontThemes[p.$size || defaultControlSize]};
  }

  &[data-open="false"] {
    &:focus-visible {
      &:before {
        display: block;
      }
    }
  }

  &:focus,
  &:focus-within {
    outline-color: transparent;
  }

  ${(p) => p.$invalid
    ? css `
          &[data-open="true"] {
            ${outlineBorderActiveRedCSS};
          }
        `
    : css `
          &[data-open="true"] {
            ${outlineBorderActiveBlueCSS};
          }
        `}
  &:before {
    box-sizing: border-box;
    position: absolute;
    display: none;
    content: "";
    top: -5px;
    left: -5px;
    right: 0;
    bottom: 0;
    height: calc(10px + 100%);
    width: calc(10px + 100%);
    border-radius: 10px;
    border: 2px solid ${(p) => p.theme.systemColors.blue600};
  }
`;
const Component = (props) => {
    return (_jsx(USelectProviders, { onSearch: props.onSearch, onDropdownHidden: props.onDropdownHidden, onDropdownShow: props.onDropdownShow, children: _jsx(USelectGeneralComponent, { ...props }) }));
};
export const UltraSelect = Object.assign(Component, {
    Control,
    ValueWrapper,
    Arrow,
    Dropdown: USelectDropdown.Wrapper,
    DropdownOptions: SelectDropdownOptions.Wrapper,
});
